import { DropdownOptionModel } from 'ext-ui/components/Dropdown/components/DropdownOption/DropdownOption'
import { DropdownOptionGroup } from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import React, { useMemo, useState } from 'react'
import { CreditCardDropdownOptionDetails } from 'src/components/CreditCardDropdownV2/CreditCardDropdownOption'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardType } from 'src/models/creditCard.models'
import { useQueryCreditCards } from 'src/queries/creditCard.queries'
import { queryCacheOptions } from 'src/queries/queries.constants'
import { InfiniteScrollProps } from 'src/types'

interface UseCreditCardDropdownOptionsParams {
  location?: string,
  search?: string
}

interface UseCreditCardDropdownOptionsResult {
  optionGroups: DropdownOptionGroup<CreditCardDropdownOptionDetails>[],
  infiniteScrollProps: InfiniteScrollProps<string>
  isLoading: boolean
  searchQuery: string
  setSearchQuery: (query: string) => void
}

export const useCreditCardDropdownOptions = (
  params: UseCreditCardDropdownOptionsParams,
): UseCreditCardDropdownOptionsResult => {
  const translate = useTranslate()
  const [searchQuery, setSearchQuery] = useState<string>(params.search || '')

  const filters = useMemo(
    () => ({
      search: searchQuery,
      type: CreditCardType.Source,
      count: 100,
      sortField: 'type',
      sortDirection: 'ASC',
    }),
    [searchQuery],
  )

  const {
    data: creditCardsData,
    fetchNextPage: fetchMoreCreditCards,
    isFetching: creditCardsLoading,
    complete: creditCardsComplete,
  } = useQueryCreditCards({
    filters,
    queryCacheOptions: queryCacheOptions({staleTime: 10 * 60 * 1000, gcTime: 10 * 60 * 1000}),
    additionalKey: params.location,
  })

  const optionGroups: DropdownOptionGroup<CreditCardDropdownOptionDetails>[] = useMemo(() => {
    if (!creditCardsData) return []

    return creditCardsData.creditCards.reduce(
      (acc, cur) => {
        acc[0].options.push({
          value: cur.id,
          label: cur.truncatedDisplayName,
          sublabel: undefined,
          details: {
            isBudget: cur.isBudget,
            imageUrl: cur.isBudget ? cur.cardTypeImageSrc : cur.cardImage.urls.small,
          },
          disabled: false,
        })
        return acc
      },
      [
        {
          id: 'cardAccounts',
          heading: (
            <>
              {translate('Card Accounts')}
            </>
          ),
          options: [] as DropdownOptionModel<CreditCardDropdownOptionDetails>[],
        }
      ],
    )

  }, [creditCardsData, translate])

  const infiniteScrollProps: InfiniteScrollProps<string> = useMemo(
    () => ({
      fetch: (filterInput, page) => {
        if (page === 0) {
          setSearchQuery(filterInput || '')
        } else {
          fetchMoreCreditCards()
        }
      },
      page: 0,
      loading: creditCardsLoading,
      complete: creditCardsComplete,
    }),
    [fetchMoreCreditCards, creditCardsLoading, creditCardsComplete],
  )

  return {
    optionGroups,
    infiniteScrollProps,
    isLoading: creditCardsLoading,
    searchQuery,
    setSearchQuery,
  }
}
